import type { WorkRights } from "@/lib/music/analyzer-types";

/**
 * Ce que l'écran d'import dit des droits, juste avant de valider.
 *
 * # Pourquoi ici et pas après le dépôt
 *
 * C'est le seul moment où la personne peut encore corriger le compositeur. La
 * détection se fait sur son nom (`docs/spec/droits-a-l-import.md`), donc une
 * erreur de résolution se répare en deux frappes tant que le champ est sous les
 * doigts. Après, elle découvrirait une pastille sur une carte sans savoir d'où
 * elle sort ni comment la contredire.
 *
 * # On ne dit jamais plus que ce qu'on sait
 *
 * `restricted` **affirme** que l'œuvre est protégée : la date de naissance ou de
 * décès du compositeur le prouve. On peut donc l'écrire.
 *
 * Un verdict de domaine public, lui, ne porte que sur l'**œuvre**. Le fichier
 * déposé reste d'origine inconnue, et c'est pour ça que le serveur laisse la
 * pièce en `unknown` malgré la bonne nouvelle. La phrase le dit dans cet
 * ordre : ce qui est acquis, puis ce qui ne l'est pas.
 *
 * # Aucune alarme
 *
 * Ni couleur d'erreur, ni point d'exclamation, ni « attention ». Personne n'a
 * rien fait de mal en déposant une chanson qu'il travaille : la mention est une
 * information sur ce qui va se passer, pas un reproche. Le ton du produit, déjà
 * tenu dans `journey-glance.ts`.
 *
 * Module pur : ni React, ni DOM. Testé dans `__tests__/work-rights-notice.test.ts`.
 */

export type WorkRightsNotice = {
  /** La conséquence, en une phrase. C'est elle qui compte. */
  readonly consequence: string;
  /**
   * Sur quoi le verdict s'appuie, tel que le serveur l'a écrit.
   *
   * De la **donnée**, pas de la copie : elle vient d'un script et de dates
   * lues dans une base extérieure. D'où le nettoyage ci-dessous.
   */
  readonly reason: string;
};

/**
 * La mention à afficher, ou `null` quand il n'y a rien à dire.
 *
 * `null` couvre le cas le plus fréquent d'un fonds personnel : un compositeur
 * que la base extérieure ne résout pas franchement. Ne rien dire est alors la
 * seule réponse honnête, et c'est aussi la moins bruyante.
 */
export function workRightsNotice(
  rights: WorkRights | null | undefined,
): WorkRightsNotice | null {
  if (rights === null || rights === undefined) {
    return null;
  }

  return {
    consequence: rights.blocksPublication
      ? "Ce morceau est sous droits. Il reste dans ta bibliothèque, visible de toi seul."
      : "L'œuvre est dans le domaine public. Ce fichier reste dans ta bibliothèque, sa provenance n'étant pas établie.",
    reason: cleanServerText(rights.note),
  };
}

/**
 * Le « — » est banni du texte affiché de Virtuosa, et cette chaîne vient du
 * serveur, où elle a été écrite à des dates différentes par un script et par
 * des humains. On la nettoie au rendu plutôt que de parier qu'elle est propre.
 */
function cleanServerText(text: string): string {
  return text.replace(/\s+—\s+/g, " ; ").replace(/—/g, ",");
}
